import { useState, useCallback } from 'react';
import { useAsyncState } from '@/shared/hooks/useAsyncState';
import { tenantService } from '../services/tenantService';
import type { Tenant, CreateTenantData, UpdateTenantData } from '../types';

export const useSuperadminTenants = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [lastFetch, setLastFetch] = useState<number>(0);
  const { loading, error, handleAsyncOperation } = useAsyncState();

  const fetchTenants = useCallback(async (force = false) => {
    // Se já temos dados e não passou muito tempo desde a última busca (5 minutos)
    const now = Date.now();
    if (!force && tenants.length > 0 && now - lastFetch < 5 * 60 * 1000) {
      return;
    }

    await handleAsyncOperation(async () => {
      const data = await tenantService.fetchTenants();
      setTenants(data);
      setLastFetch(now);
    });
  }, [tenants, lastFetch, handleAsyncOperation]);

  const createTenant = async (tenantData: CreateTenantData) => {
    await handleAsyncOperation(async () => {
      const { logoFile, ativo, tipo_unidade, ...rest } = tenantData;
      const newTenant = await tenantService.createTenant({
        ...rest,
        status: ativo === false ? 'INATIVO' : 'ATIVO',
        tipo_unidade: tipo_unidade ?? 'ASSOCIADO',
        matriz_id: 1,
      } as CreateTenantData & { status: string; matriz_id: number });

      // 2. Fazer upload da logo se existir
      if (logoFile && newTenant?.id) {
        try {
          const storageService = new (await import('@/services/StorageService')).StorageService();
          const logoUrl = await storageService.uploadLogo(newTenant.id, logoFile);

          // 3. Atualizar o tenant com a URL da logo
          await tenantService.updateTenant({
            id: newTenant.id,
            nome: newTenant.nome,
            ativo: newTenant.status === 'ATIVO',
            logo_url: logoUrl
          });
        } catch (error) {
          console.error("Erro ao fazer upload da logo:", error);
          // O tenant foi criado, só falhou a logo.
        }
      }

      await fetchTenants(true);
    });
  };

  const updateTenant = async (tenantData: UpdateTenantData) => {
    await handleAsyncOperation(async () => {
      let finalData = { ...tenantData };
      const { logoFile } = finalData;

      if (logoFile && tenantData.id) {
        try {
          const storageService = new (await import('@/services/StorageService')).StorageService();
          const logoUrl = await storageService.uploadLogo(tenantData.id, logoFile);
          finalData.logo_url = logoUrl;
        } catch (error) {
          console.error("Erro ao fazer upload da logo:", error);
        }
      }

      await tenantService.updateTenant(finalData);
      await fetchTenants(true);
    });
  };
  const toggleTenantStatus = async (id: string, active: boolean) => {
    await handleAsyncOperation(async () => {
      await tenantService.toggleTenantStatus(id, active);
      await fetchTenants(true);
    });
  };

  const deleteTenant = async (id: string) => {
    await handleAsyncOperation(async () => {
      await tenantService.deleteTenant(id);
      setTenants(prev => prev.filter(t => t.id !== id));
    });
  };

  return {
    tenants,
    loading,
    error,
    fetchTenants,
    createTenant,
    updateTenant,
    toggleTenantStatus,
    deleteTenant
  };
}; 